/**
 * Icon components using lucide-react.
 * Each export wraps a Lucide icon at the size used by the original inline SVGs,
 * so every consumer keeps working without changes.
 *
 * 3D-mode icons (Edit, Sculpt) use custom inline SVGs designed to match Lucide's
 * visual weight: 24×24 viewBox, 2px stroke, round caps/joins, currentColor.
 *
 * All exports accept an optional `size` prop so they can be used at any size
 * (e.g. size={13} in header/sidebar controls, size={17} in toolbar mode buttons).
 */

import {
  Aperture,
  Bone,
  Box,
  Camera,
  Circle,
  Crosshair,
  Diamond,
  Eraser,
  Eye,
  EyeOff,
  Feather,
  Focus,
  Image,
  Maximize2,
  Move,
  Network,
  Pencil,
  Plus,
  Redo2,
  Rotate3d,
  RotateCcw,
  Save,
  Star,
  Triangle,
  Undo2,
} from "lucide-react";
import {
  BrushDrawIcon as BrushDrawIcon3D,
  BrushFlattenIcon as BrushFlattenIcon3D,
  BrushGrabIcon as BrushGrabIcon3D,
  BrushInflateIcon as BrushInflateIcon3D,
  BrushMaskIcon as BrushMaskIcon3D,
  BrushPinchIcon as BrushPinchIcon3D,
  BrushRelaxIcon as BrushRelaxIcon3D,
  BrushSmoothIcon as BrushSmoothIcon3D,
  SnapCursorToIcon as SnapCursorToIcon3D,
  SnapToCursorIcon as SnapToCursorIcon3D,
} from "./icons3d.tsx";

/** Size used by toolbar icons (matches the old 17×17 render) */
const TB = 17;
/** Size used by hierarchy type icons */
const SM = 12;
/** Size used by eye icons */
const EYE = 14;

export function NavigateIcon({ size = TB }: { size?: number } = {}) {
  return <Rotate3d size={size} />;
}

export function PrecisionIcon({ size = TB }: { size?: number } = {}) {
  return <Crosshair size={size} />;
}

export function MoveIcon({ size = TB }: { size?: number } = {}) {
  return <Move size={size} />;
}

export function RotateIcon({ size = TB }: { size?: number } = {}) {
  return <RotateCcw size={size} />;
}

export function ScaleIcon({ size = TB }: { size?: number } = {}) {
  return <Maximize2 size={size} />;
}

export function MatcapIcon({ size = TB }: { size?: number } = {}) {
  return <Circle size={size} fill="currentColor" strokeWidth={0} />;
}

export function SceneIcon({ size = TB }: { size?: number } = {}) {
  return <Network size={size} />;
}

export function CameraIcon({ size = TB }: { size?: number } = {}) {
  return <Camera size={size} />;
}

export function ImageIcon({ size = TB }: { size?: number } = {}) {
  return <Image size={size} />;
}

export function MeshIcon({ size = SM }: { size?: number } = {}) {
  return <Triangle size={size} fill="currentColor" strokeWidth={0} />;
}

export function ArmatureIcon({ size = SM }: { size?: number } = {}) {
  return <Diamond size={size} strokeWidth={2.5} />;
}

export function ArmatureIconLg({ size = TB }: { size?: number } = {}) {
  return <Bone size={size} />;
}

export function EmptyIcon({ size = SM }: { size?: number } = {}) {
  return <Circle size={size} strokeWidth={2.5} />;
}

export function EyeOpenIcon({ size = EYE }: { size?: number } = {}) {
  return <Eye size={size} />;
}

export function EyeClosedIcon({ size = EYE }: { size?: number } = {}) {
  return <EyeOff size={size} />;
}

export function UndoIcon({ size = TB }: { size?: number } = {}) {
  return <Undo2 size={size} />;
}

export function RedoIcon({ size = TB }: { size?: number } = {}) {
  return <Redo2 size={size} />;
}

export function FocusIcon({ size = TB }: { size?: number } = {}) {
  return <Focus size={size} />;
}

export function StarIcon({ size = TB }: { size?: number } = {}) {
  return <Star size={size} />;
}

export function StarFilledIcon({ size = TB }: { size?: number } = {}) {
  return <Star size={size} fill="currentColor" />;
}

export function AddIcon({ size = TB }: { size?: number } = {}) {
  return <Plus size={size} />;
}

/** Mesh edit mode — triangle wireframe with vertex dots. */
export function EditModeIcon({ size = TB }: { size?: number } = {}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 4L4 20h16Z" />
      <circle cx={12} cy={4} r={3} fill="currentColor" stroke="none" />
      <circle cx={4} cy={20} r={3} fill="currentColor" stroke="none" />
      <circle cx={20} cy={20} r={3} fill="currentColor" stroke="none" />
    </svg>
  );
}

export function BoxIcon({ size = TB }: { size?: number } = {}) {
  return <Box size={size} />;
}

export function RenderIcon({ size = TB }: { size?: number } = {}) {
  return <Aperture size={size} />;
}

/** Inking mode — pen nib for stroke-based inking. */
export function InkingIcon({ size = TB }: { size?: number } = {}) {
  return <Feather size={size} />;
}

/** Sculpt mode — surface with a displacement bump. */
export function SculptIcon({ size = TB }: { size?: number } = {}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 18c3 0 5-10 10-10s7 10 10 10" />
      <path d="M2 22h20" opacity={0.35} />
    </svg>
  );
}

/** Sketch mode - pencil. */
export function SketchIcon({ size = TB }: { size?: number } = {}) {
  return <Pencil size={size} />;
}

export function SaveIcon({ size = TB }: { size?: number } = {}) {
  return <Save size={size} />;
}

export function OperatorsIcon({ size = TB }: { size?: number } = {}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <text
        x="12"
        y="11"
        textAnchor="middle"
        dominantBaseline="central"
        fill="currentColor"
        stroke="none"
        fontSize="10"
        fontWeight="700"
        fontFamily="system-ui, sans-serif"
      >
        OPS
      </text>
      <line x1="4" y1="19" x2="20" y2="19" />
    </svg>
  );
}

export function ProjectIcon({ size = TB }: { size?: number } = {}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <text
        x="12"
        y="11"
        textAnchor="middle"
        dominantBaseline="central"
        fill="currentColor"
        stroke="none"
        fontSize="9"
        fontWeight="700"
        fontFamily="system-ui, sans-serif"
      >
        PROJ
      </text>
      <line x1="4" y1="19" x2="20" y2="19" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Sculpt brush icons
// ---------------------------------------------------------------------------

export function BrushDrawIcon({ size = TB }: { size?: number } = {}) {
  return <BrushDrawIcon3D size={size} />;
}

export function BrushSmoothIcon({ size = TB }: { size?: number } = {}) {
  return <BrushSmoothIcon3D size={size} />;
}

export function BrushFlattenIcon({ size = TB }: { size?: number } = {}) {
  return <BrushFlattenIcon3D size={size} />;
}

export function BrushGrabIcon({ size = TB }: { size?: number } = {}) {
  return <BrushGrabIcon3D size={size} />;
}

export function BrushInflateIcon({ size = TB }: { size?: number } = {}) {
  return <BrushInflateIcon3D size={size} />;
}

export function BrushPinchIcon({ size = TB }: { size?: number } = {}) {
  return <BrushPinchIcon3D size={size} />;
}

export function BrushMaskIcon({ size = TB }: { size?: number } = {}) {
  return <BrushMaskIcon3D size={size} />;
}

export function BrushRelaxIcon({ size = TB }: { size?: number } = {}) {
  return <BrushRelaxIcon3D size={size} />;
}

/** Clay strips — stacked bold horizontal strokes tapering inward. */
export function BrushClayStripsIcon({ size = TB }: { size?: number } = {}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 7h18" />
      <path d="M5 12h14" />
      <path d="M7 17h10" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Sketch tool icons
// ---------------------------------------------------------------------------

export function SketchPencilIcon({ size = TB }: { size?: number } = {}) {
  return <Pencil size={size} />;
}

export function BrushEraseIcon({ size = TB }: { size?: number } = {}) {
  return <Eraser size={size} />;
}

// ---------------------------------------------------------------------------
// Cursor snap icons
// ---------------------------------------------------------------------------

export function SnapToCursorIcon({ size = TB }: { size?: number } = {}) {
  return <SnapToCursorIcon3D size={size} />;
}

export function SnapCursorToIcon({ size = TB }: { size?: number } = {}) {
  return <SnapCursorToIcon3D size={size} />;
}
